import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_ORDERS } from '../graphql/queries'
import { Order, OrderFilters } from '../types/order'
import { parseApiDate } from '../lib/format'
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'react-router-dom'

const REFRESH_INTERVAL_MS = 5000

const CustomerDisplayPage: React.FC = () => {
  const [filters] = useState<OrderFilters>({})
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [searchParams] = useSearchParams()

  const { data, refetch } = useQuery(GET_ORDERS, {
    variables: { filters },
    fetchPolicy: 'cache-first'
  })

  useEffect(() => {
    const interval = setInterval(() => {
      refetch()
    }, REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [refetch])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Handle escape key to exit fullscreen
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isFullScreen) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      }
      setIsFullScreen(false)
    }
  }

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullScreen])

  // Exit fullscreen when component unmounts
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [])

  // Listen for fullscreen changes to keep state in sync
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Auto-enter fullscreen when component mounts (if not already in fullscreen)
  useEffect(() => {
    const autoFullscreen = searchParams.get('fullscreen') !== 'false'
    if (autoFullscreen && !document.fullscreenElement) {
      const timer = setTimeout(() => {
        document.documentElement.requestFullscreen().catch(() => {
        })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [searchParams])

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {
      })
    } else {
      document.exitFullscreen()
    }
  }

  const orders: Order[] = data?.orders || []

  const visibleOrders = useMemo(() => {
    return orders
      .filter(o => o.status === 'CONFIRMED' || o.status === 'PREPARING' || o.status === 'READY')
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
  }, [orders])

  // current_status_since / created_at are ISO (API 2026-10-08) — lib/format.ts parses them.
  const formatTime = (iso: string | undefined | null) =>
    parseApiDate(iso)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? 'N/A'

  const confirmedOrders = visibleOrders.filter(o => o.status === 'CONFIRMED')
  const preparingOrders = visibleOrders.filter(o => o.status === 'PREPARING')
  const readyOrders = visibleOrders.filter(o => o.status === 'READY')

  return (
    <div className={`min-h-screen bg-black text-white ${isFullScreen ? 'fixed inset-0 z-50' : ''}`}>
      <div className={`${isFullScreen ? 'h-full flex flex-col px-8 py-8' : 'max-w-7xl mx-auto px-6 py-6'}`}>
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center space-x-4">
            <h1 className={`${isFullScreen ? 'text-7xl' : 'text-5xl'} font-black tracking-tight`}>Orders</h1>
          </div>
          <div className="flex items-center space-x-8">
            <div className={`${isFullScreen ? 'text-4xl' : 'text-xl'} font-medium opacity-80`}>{currentTime.toLocaleTimeString()}</div>
            <button
              onClick={toggleFullScreen}
              className="p-3 hover:bg-gray-800 rounded-full transition-colors"
              title={isFullScreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullScreen ? (
                <ArrowsPointingInIcon className="h-10 w-10" />
              ) : (
                <ArrowsPointingOutIcon className="h-8 w-8" />
              )}
            </button>
          </div>
        </div>

        <div className={`flex-1 grid grid-cols-1 md:grid-cols-3 gap-10 ${isFullScreen ? 'overflow-hidden' : ''}`}>
          {/* Confirmed Orders */}
          <div className="flex flex-col">
            <h2 className={`${isFullScreen ? 'text-5xl' : 'text-4xl'} font-bold text-blue-400 mb-8 flex items-center`}>
              <span className="w-4 h-4 bg-blue-400 rounded-full mr-4"></span>
              Confirmed
            </h2>
            <div className={`space-y-4 ${isFullScreen ? 'overflow-y-auto pr-2' : ''}`}>
              {confirmedOrders.map(o => (
                <div key={o.id} className="bg-blue-900/20 border border-blue-700/50 rounded-2xl p-6 text-center transform transition-all">
                  <div className={`${isFullScreen ? 'text-6xl' : 'text-4xl'} font-extrabold tracking-wider`}>{o.order_number}</div>
                  <div className={`mt-2 ${isFullScreen ? 'text-lg' : 'text-sm'} text-blue-300`}>Placed {formatTime(o.current_status_since || o.created_at)}</div>
                </div>
              ))}
              {confirmedOrders.length === 0 && (
                <div className="text-center py-12 text-gray-500 italic">No confirmed orders</div>
              )}
            </div>
          </div>

          {/* Preparing Orders */}
          <div className="flex flex-col">
            <h2 className={`${isFullScreen ? 'text-5xl' : 'text-4xl'} font-bold text-yellow-300 mb-8 flex items-center`}>
              <span className="w-4 h-4 bg-yellow-300 rounded-full mr-4"></span>
              Preparing
            </h2>
            <div className={`space-y-4 ${isFullScreen ? 'overflow-y-auto pr-2' : ''}`}>
              {preparingOrders.map(o => (
                <div key={o.id} className="bg-yellow-900/20 border border-yellow-700/50 rounded-2xl p-6 text-center transform transition-all animate-pulse">
                  <div className={`${isFullScreen ? 'text-6xl' : 'text-4xl'} font-extrabold tracking-wider`}>{o.order_number}</div>
                  <div className={`mt-2 ${isFullScreen ? 'text-lg' : 'text-sm'} text-yellow-200`}>Placed {formatTime(o.current_status_since || o.created_at)}</div>
                </div>
              ))}
              {preparingOrders.length === 0 && (
                <div className="text-center py-12 text-gray-500 italic">No orders in preparation</div>
              )}
            </div>
          </div>

          {/* Ready Orders */}
          <div className="flex flex-col">
            <h2 className={`${isFullScreen ? 'text-5xl' : 'text-4xl'} font-bold text-green-400 mb-8 flex items-center`}>
              <span className="w-4 h-4 bg-green-400 rounded-full mr-4"></span>
              Ready
            </h2>
            <div className={`space-y-4 ${isFullScreen ? 'overflow-y-auto pr-2' : ''}`}>
              {readyOrders.map(o => (
                <div key={o.id} className="bg-green-600/20 border-2 border-green-500 rounded-2xl p-8 text-center transform transition-all shadow-lg shadow-green-500/20">
                  <div className={`${isFullScreen ? 'text-8xl' : 'text-5xl'} font-black tracking-tighter`}>{o.order_number}</div>
                  <div className={`mt-3 ${isFullScreen ? 'text-xl' : 'text-base'} text-green-300 font-medium`}>Ready for pickup!</div>
                </div>
              ))}
              {readyOrders.length === 0 && (
                <div className="text-center py-12 text-gray-500 italic">No ready orders</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerDisplayPage















